import { WebAuth } from 'auth0-js';
import jwtDecode from 'jwt-decode';
import has from 'lodash.has';

import '../helpers/ie-polyfill';
import getFormattedUrl from '../helpers/getFormattedUrl';
import getParamByName from '../helpers/getParamByName';
import getRootDomain from '../helpers/getRootDomain';
import normalize from '../helpers/normalizeErrorPayload';
import pardot from '../../helpers/pardot';
import parseHash from '../helpers/parseHash';
import parseUTMCookie from '../helpers/parseUtmCookie';
import setGeoLoc from '../helpers/setGeoLocationData';
import socialProof from '../helpers/socialProof';
import titleCase from '../helpers/titleCase';

import Logger from '../helpers/logger';

const log = new Logger();

const userAgent = navigator.userAgent || navigator.vendor || navigator.platform || window.opera;
const isKeynote = userAgent.includes('KeynoteTest');
const isMobile = ~userAgent.search(/linux.*android|iphone|windows\s+(?:ce|phone)/i);
const isTablet = ~userAgent.search(/android|ipad|playbook|silk/i);

const SIGN_IN_STEP = 'signIn';
const SIGN_UP_STEP = 'signUp';

function freeTrialController(
  $scope,
  $window,
  $timeout,
  $interval,
  $cookies,
  $location,
  Login,
  Trial,
  ThirdParty,
  BigQuery,
  vcRecaptchaService
) {
  /**
   * -----------------------------------------------------------------
   * SCOPED VARIABLES/FUNCTIONS
   * Available to the views
   * ------------------------------------------------------------------
   */

  // Variables
  $scope.allForm = false;
  $scope.demo = {};
  $scope.redirectMsg = 'Redirecting to your new store...';
  $scope.step = 'form';
  $scope.showRecaptcha = true;

  $scope.error = false;
  $scope.errorMessage = '';

  $scope.progressStyle = { right: '100%' };
  $scope.progressValue = 0;

  $scope.newTrialists = socialProof();

  /**
   * -----------------------------------------------------------------
   * INTERNAL FUNCTIONS/VARIABLES
   * -----------------------------------------------------------------
   */

  // Auth0 variables
  let auth0 = null;
  let connection = '';
  let tenantId = 'default';
  // let accessToken = '';
  let idToken = '';

  // Misc variables
  const domain = getRootDomain($location.host());
  let optimizely = 'default';
  // let form = '';
  let version = 'v1';
  let recaptchaID = 0;
  let storeName = '';
  let storeUrl = '';
  let createdOn = '';

  // Cookie variables
  const cookieDefaults = {
    domain: domain,
    path: '/',
    secure: true
  };

  /**
   * Find and set what kind of device we're working with
   */
  const setDevice = () => {
    $scope.demo.deviceType = 'desktop';

    if (isMobile) {
      $scope.demo.deviceType = 'mobile';
    } else if (isTablet) {
      $scope.demo.deviceType = 'tablet';
    }

    log.debug({
      category: 'controller',
      message: 'setDevice',
      data: {
        device: $scope.demo.deviceType
      }
    });
  };

  /**
   * Sets hidden fields, and adds any version-specific fields
   *
   * @param data
   */
  const setHiddenFields = () => {
    const data = (version === 'v2') ? [{ name: 'planId', type: 'string', value: process.env.TRIALS.PLAN_ID }] : [];
    log.info('app.setHiddenFields', data);


    // UserAgent
    $scope.demo.userAgent = userAgent;

    // Lead info
    $scope.demo.leadSource = $window.leadSource || 'New Ecommerce Deal Demo';
    $scope.demo.leadDescription = $window.leadDescription || getParamByName('leadDescription') || '';

    // Set Geolocation
    $scope.demo.Geo_Continent__c = '';
    $scope.demo.Geo_Country__c = '';
    $scope.demo.Geo_State__c = '';
    $scope.demo.Geo_City__c = '';
    $scope.demo.Geo_Postal_Code__c = '';
    $scope.demo.Geo_Time_Zone__c = '';
    $scope.demo.Geo_Latitude__c = 0.0;
    $scope.demo.Geo_Longitude__c = 0.0;
    $scope.demo.Geo_Ip__c = '';
    $scope.demo = setGeoLoc($scope.demo);

    // Source, Medium, Campaign, Term from utmz cookie
    const utmz = $cookies.get('__utmz') || '';
    $scope.demo.source = 'none';
    $scope.demo.medium = 'none';
    $scope.demo.campaign = 'none';
    $scope.demo.keyword = 'none';

    if (utmz !== undefined && utmz !== '') {
      $scope.demo.source = parseUTMCookie(utmz, 'utmcsr');
      $scope.demo.medium = parseUTMCookie(utmz, 'utmcmd');
      $scope.demo.campaign = parseUTMCookie(utmz, 'utmccn');
      $scope.demo.keyword = parseUTMCookie(utmz, 'utmctr');
    }

    // Optimizely Test Variations
    const pricingPlanTestId = '14500360338'; // Quick Wins Test May072019
    $scope.demo.Pricing_Plans = 'not found';

    $window.optimizely = $window.optimizely || [];

    //OPTIMIZELY QUICK WINS TEST - MAY072019
    function pricingPlanTestCheck() {
      return (
        $cookies.get('pv1')
        || $cookies.get('pv2')
        || $cookies.get('DemoLoginToken')
        || $cookies.get('V2TrialLoginToken')
      );
    }
    try {
      if (!pricingPlanTestCheck()) {
        $window.optimizely.push({
          "type": "user",
          "attributes": {
            "noTrialToken": "true"
          }
        });
        $window.optimizely.push({
          type: 'page',
          pageName: '8461460092_pricing_test_all_www_not_blog'
        });
      }
    } catch (err) {
      log.exception(err);
    }
    //END QUICK WINS TEST

    try {
      optimizely = JSON.stringify($window.optimizely.get('state').getVariationMap());
    } catch (err) {
      log.exception(err);
    }

    // Checking Pricing Test
    if (version === 'v2') {
      try {
        $scope.demo.Pricing_Plans = $window.optimizely
          .get('state')
          .getDecisionObject({ campaignId: pricingPlanTestId })
          .variation;
      } catch (err) {
        log.error({
          category: 'controller',
          message: 'setHiddenFields.PricingPlanTest',
          data: {
            error: err
          }
        });
      }
    }

    // GA Client ID
    const gaCookie = $cookies.get('_ga') || '';
    const gaCrumbs = gaCookie.split('.');
    $scope.demo.gaClientID = (gaCookie) ? `${gaCrumbs[2]}.${gaCrumbs[3]}` : 'none';

    // Out dated vars that are required by the service
    const vhsbc = $cookies.get('vHSBC') || '';
    const ftTellapalId = 'none';
    const ftWebsite = 'none';
    const ftCountry = 'us';
    const ftUserIpAddress = '111.111.11.11';

    // Referrer and Landing Page (deprecated)
    const ftRefArray = vhsbc
      .split('&')
      .map(pair => pair.split('=')[1]);

    let ftReferrer = ftRefArray[0] || 'none';
    let ftLandingPage = ftRefArray[1] || 'none';

    // Referrer and Landing Page
    $scope.demo.referrer = ftRefArray[0] || $cookies.get('calltrk_referrer') || 'none';
    $scope.demo.landingpage = ftRefArray[1] || $cookies.get('calltrk_landing') || 'none';

    const tempUrlArray = $window.location.pathname.split('/');

    if (tempUrlArray[1] === 'free' && tempUrlArray[2]) {
      [,, ftReferrer] = tempUrlArray;
      ftLandingPage = ftReferrer;
    }

    // Set hidden demo object attributes
    $scope.demo.attributes = [
      { name: 'productVersion', type: 'string', value: version },
      { name: 'Custom_Field_Competitr2', type: 'string', value: 'default.asp' },
      { name: 'industry', type: 'string', value: 'other' },
      { name: 'websiteAddress', type: 'string', value: ftWebsite },
      { name: 'geoCode', type: 'string', value: ftCountry },
      { name: 'country', type: 'string', value: ftCountry },
      { name: 'source', type: 'string', value: $scope.demo.source },
      { name: 'medium', type: 'string', value: $scope.demo.medium },
      { name: 'campaign', type: 'string', value: $scope.demo.campaign },
      { name: 'term', type: 'string', value: $scope.demo.keyword },
      { name: 'referrer', type: 'string', value: ftReferrer },
      { name: 'landing', type: 'string', value: ftLandingPage },
      { name: 'ipAddress', type: 'string', value: ftUserIpAddress },
      { name: 'TellapalImpressionID', type: 'string', value: ftTellapalId },
      { name: 'plan', type: 'string', value: 'none' },
      { name: 'whenToBuy', type: 'string', value: 'none' },
      ...data
    ];

    log.debug({
      category: 'controller',
      message: 'setHiddenFields',
      data: {
        attributes: $scope.demo.attributes
      }
    });
  };

  /**
   * Checks version cookie to know which product version to
   * assign them to. Also adds necessary form values based
   * on version.
   */
  const checkVersion = () => {
    const productVersion = document.getElementById('productVersion');
    version = (productVersion) ? productVersion.value : 'v1';

    log.debug({
      category: 'controller',
      message: 'checkVersion',
      data: { version }
    });
  };

  /**
   * -----------------------------------------------------------------
   * Recaptcha
   * -----------------------------------------------------------------
   */

  /**
   * Called when the invisible recaptcha is first created
   *
   * @param id
   */
  function widgetCreate(id) {
    log.debug({
      category: 'controller',
      message: 'widgetCreate',
      data: {
        id,
        isKeynote
      }
    });

    if (!isKeynote) recaptchaID = id;

    $scope.demo.recaptcha = 'afcb3e5a3a944065a487b31ed7f87d3d';
  }

  /**
   * Called when the recaptcha test has been submitted
   *
   * @param response
   */
  function widgetSuccess(response) {
    const start = response.substr(0, 4);
    const end = response.substr(-4);
    log.debug({
      category: 'controller',
      message: 'widgetSuccess',
      data: {
        response: `${start}...${end}`
      }
    });

    if (!isKeynote) {
      $scope.demo.recaptcha = response;
    }

    checkFormValidity();
  }

  /**
   * Called when the recaptcha expires
   */
  function widgetExpire() {
    log.debug({
      category: 'controller',
      message: 'widgetExpire'
    });

    // Kind of jank workaround for resetting recaptcha on invalid form submission
    $scope.showRecaptcha = false;
    $timeout(() => {
      $scope.showRecaptcha = true;
    });

    $scope.demo.recaptcha = null;
  }

  /**
   * Called by the form on submit
   */
  function widgetInvoke() {
    log.debug({
      category: 'controller',
      message: 'widgetInvoke',
      data: { isKeynote }
    });

    // If this is keynote, we don't need to challenge them
    if (isKeynote) {
      checkFormValidity();
      return;
    }

    vcRecaptchaService.execute(recaptchaID);
  }


  /**
   * -----------------------------------------------------------------
   * SOCIAL SIGN ON
   * -----------------------------------------------------------------
   */

  /**
   * Initializes Auth0 WebAuth
   */
  const initAuth0 = () => {
    log.debug({
      category: 'controller',
      message: 'initAuth0'
    });

    // Initialize our Auth0 lock object
    auth0 = new WebAuth({
      audience: process.env.AUTH0.AUDIENCE,
      clientID: process.env.AUTH0.CLIENT_ID,
      domain: process.env.AUTH0.DOMAIN,
      redirectUri: $window.location.href,
      responseType: 'token id_token',
      scope: 'openid profile email'
    });
  };

  /**
   * Initiate Auth0 Google sign-up
   */
  function googleLogin() {
    if (auth0 === null) {
      initAuth0();
    }

    connection = 'google-oauth2';
    auth0.authorize({
      connection: connection,
      state: JSON.stringify({ connection: connection, step: SIGN_UP_STEP })
    });

    log.debug({
      category: 'controller',
      message: 'googleLogin'
    });
  }

  /**
   * Initiate Auth0 Facebook sign-up
   */
  function facebookLogin() {
    if (auth0 === null) {
      initAuth0();
    }

    connection = 'facebook';
    auth0.authorize({
      connection: connection,
      scope: 'openid profile email',
      state: JSON.stringify({ connection: connection, step: SIGN_UP_STEP })
    });

    log.debug({
      category: 'controller',
      message: 'facebookLogin'
    });
  }

  /**
   * Initiate Auth0 LinkedIn sign-up
   */
  function linkedInLogin() {
    if (auth0 === null) {
      initAuth0();
    }

    connection = 'linkedin';
    auth0.authorize({
      connection: connection,
      scope: 'openid profile email',
      state: JSON.stringify({ connection: connection, step: SIGN_UP_STEP })
    });

    log.debug({
      category: 'controller',
      message: 'linkedInLogin'
    });
  }

  /**
   * Looks for the presence of a hash in the URL that contains specific SSO-related parameters
   *
   * @returns {boolean}
   */
  const isSsoRedirect = () => {
    return ($window.location.hash !== '' && (
      /id_token=/.test($window.location.hash)
      || /access_token=/.test($window.location.hash)
      || /error=/.test($window.location.hash)
    ));
  };

  /**
   * This is to handle the Auth0 redirect so we can get user info out of the hash in the URL
   */
  const handleSsoRedirect = () => {
    log.debug({
      category: 'controller',
      message: 'handleSsoRedirect',
      data: {
        hashStr: $window.location.hash
      }
    });

    const hashes = parseHash();

    if (!hashes || hashes.hasOwnProperty('error')) {
      processError({ status: 401, message: hashes.error || 'Error parsing hash string' });
      return;
    }

    const hasIdToken = !hashes.hasOwnProperty('id_token') || hashes.id_token === undefined;
    const hasAccessToken = !hashes.hasOwnProperty('access_token') || hashes.access_token === undefined;

    if (hasIdToken || hasAccessToken) {
      processError({ status: 401, message: 'Missing required tokens' });
      return;
    }

    if (!hashes.hasOwnProperty('state') || !hashes.state.hasOwnProperty('step')) {
      processError({ status: 401, message: 'Missing state in Auth0 hash' });
      return;
    }

    // We want to hide the form altogether if we're just redirecting to V2 admin
    if (hashes.state.step === SIGN_IN_STEP) {
      $scope.step = 'redirect';

      const token = {
        id_token: hashes.id_token,
        access_token: hashes.id_token // Hack - Seth approved
      };
      setTokensAndRedirectToLoggedIn(token);
      return;
    }

    // How to handle the initial sign-up data
    const profile = jwtDecode(hashes.id_token);

    if (!profile) {
      processError({ status: 401, message: 'There was an error parsing the JWT' });
      return;
    }

    if (!profile.hasOwnProperty('email') || !profile.hasOwnProperty('sub')) {
      processError({ status: 401, message: 'Missing email/user ID in Auth0 profile' });
      return;
    }

    // Check if we already have a v2 store with the given email address
    Trial.hasV2Trial(profile.email).then((response) => {
      const hasV2Store = response.data || false;

      log.debug({
        category: 'controller',
        message: 'handleSsoRedirect.hasV2Store',
        data: { hasV2Store }
      });

      if (hasV2Store) {
        processError({ status: 409, trialStatus: 'active', message: 'User has active V2 store' });
        return;
      }

      const tenantPropName = 'https://api.material.com/tenant';
      const hasTenantProp = profile.hasOwnProperty(tenantPropName);

      if (hasTenantProp && profile[tenantPropName] !== '') {
        $scope.step = 'redirect';
        log.debug({
          category: 'controller',
          message: 'redirecting with tenant',
          data: { hasTenantProp }
        });

        $window.location.href = '/login/v2';
        return;
      }

      const firstName = profile.given_name || '';
      const lastName = profile.family_name || '';

      capitalizeNames(firstName, lastName);
      $scope.demo.recaptcha = 'afcb3e5a3a944065a487b31ed7f87d3d'; // Bypass recaptcha with SSO
      $scope.demo.emailAddress = profile.email;
      $scope.demo.auth0UserId = profile.sub;
      // accessToken = hashes.access_token;
      idToken = hashes.id_token;
      connection = hashes.state.connection;
      handleSocialSignUp();
    });
  };

  const setTokensAndRedirectToLoggedIn = (token) => {
    $cookies.put('id_token', token.id_token, cookieDefaults);
    $cookies.put('access_token', token.access_token, cookieDefaults);

    log.debug({
      category: 'controller',
      message: 'redirecting',
      data: {
        url: process.env.TRIALS.V2_LOGIN_URL
      }
    });

    $window.location.href = process.env.TRIALS.V2_LOGIN_URL;
  };

  /**
   * Social SignUp Demo form handler
   */
  const handleSocialSignUp = () => {
    log.debug({
      category: 'controller',
      message: 'handleSocialSignUp'
    });

    // Set hidden input field with window vars and cookie values
    setHiddenFields();

    if ($scope.demo.auth0UserId === undefined) {
      processError({ message: 'No auth0UserId', status: 400 });
      return;
    }

    // form = 'demoSignUp';
    $scope.step = 'loading';

    // Referral Codes
    $scope.demo.referralCode = $cookies.get('friendCode') || $cookies.get('influencer') || '';
    $scope.demo.friendBuyReferralCode = $cookies.get('friendCode') || '';
    $scope.demo.influencerReferralCode = $cookies.get('influencer') || '';

    // SSO
    $scope.demo.SocialSignUp__c = true;
    $scope.demo.SocialSignUpNetwork__c = $scope.connection || '';

    scrollToTop();
    animateProgressBar();

    $scope.demo.fullStorySessionUrl = $cookies.get('fs_session_url');

    createTrial('social');
  };

  /**
   * Handle the social trial creation response
   *
   * @param response
   */
  const processCreateSocialResponse = (response) => {
    log.debug({
      category: 'controller',
      message: 'processCreateSocialResponse',
      data: { response }
    });

    if (response.status !== 201) {
      processError(response);
      return;
    }

    tenantId = response.data.tenantId;
    createdOn = Date.now();

    Trial.storeInfo($scope.tenantId)
      .then((storeInfo) => {
        storeName = storeInfo.data.name;
        storeUrl = storeInfo.data.storeUrl;
        createdOn = storeInfo.data.createdOn;
        processv2SocialLoginResponse(response.data);
      })
      // We don't really care if there's an error on the storeinfo service.
      // We'll still move forward with the login because we have a tenant ID.
      .catch(() => {
        processv2SocialLoginResponse(response.data);
      });
  };

  /**
   * Handle the v2 login response
   */
  const processv2SocialLoginResponse = (responseData) => {
    log.debug({
      category: 'controller',
      message: 'processv2SocialLoginResponse'
    });

    if (!idToken) {
      processError({ message: 'No ID token from social login', status: 400 });
      return;
    }

    const mergedResponse = ThirdParty.mergeResponseObject(
      {},
      version,
      $scope.demo,
      storeName,
      storeUrl,
      tenantId,
      createdOn,
      optimizely
    );

    ThirdParty.loadTrackers();
    ThirdParty.sendAnalytics(mergedResponse);
    BigQuery.sendV2MrktBQTrialCreate(mergedResponse);
    ThirdParty.sendV2Growsumo(mergedResponse);
    pardot.sendV2(mergedResponse)
      .catch((e) => {
        log.exception(e);
      });

    // After conversion pixels load, sends tracking data to Optimizely for demo conversion
    $window.optimizely = $window.optimizely || [];
    $window.optimizely.push({ type: 'event', eventName: 'SocialSignUp' }); // Optimizely X

    ThirdParty.sendV2Warehouse(mergedResponse).then(() => {
      if (auth0 === null) {
        initAuth0();
      }

      if (has(responseData, 'token.id_token') && has(responseData, 'token.access_token')) {
        $scope.step = 'redirect';
        setTokensAndRedirectToLoggedIn(responseData.token);
      } else {
        auth0.authorize({
          connection: connection,
          state: JSON.stringify({ connection: connection, step: SIGN_IN_STEP })
        });
      }
    });
  };


  /**
   * -----------------------------------------------------------------
   * FORM VALIDATION, SUBMISSION, & PROCESSING
   * -----------------------------------------------------------------
   */

  /**
   * Called when the form's submit button is clicked
   */
  function checkFormValidity() {
    log.debug({
      category: 'controller',
      message: 'checkFormValidity',
      data: {
        isValid: $scope.DemoSignUp.$valid
      }
    });

    if ($scope.DemoSignUp.$valid) {
      handleSignUpSubmit();
      return;
    }

    const scrollEl = $scope.DemoSignUp.$$element[0];

    widgetExpire();
    $scope.DemoSignUp.firstName.$setTouched();
    $scope.DemoSignUp.lastName.$setTouched();
    $scope.DemoSignUp.phone.$setTouched();
    $scope.DemoSignUp.email.$setTouched();
    $scope.DemoSignUp.password.$setTouched();
    $window.scrollBy(0, scrollEl.getBoundingClientRect().top);
  }

  const hasEmptyLastName = () => $scope.demo.lastName.toLowerCase().trim() === 'none';

  const capitalizeNames = (firstName = $scope.demo.firstName, lastName = $scope.demo.lastName) => {
    $scope.demo.firstName = titleCase(firstName);
    $scope.demo.lastName = titleCase(lastName);
  };

  const prepTrialUserName = () => {
    if (!hasEmptyLastName()) {
      capitalizeNames();
      return;
    }

    const names = $scope.demo.firstName.split(' ');

    if (names.length > 1) {
      $scope.demo.firstName = names.shift();
      $scope.demo.lastName = names.join(' ');
    }

    capitalizeNames();
  };

  /**
   * Demo form handler
   */
  const handleSignUpSubmit = () => {
    // Set hidden input field with window vars and cookie values
    setHiddenFields();

    // Referral Codes
    $scope.demo.referralCode = $cookies.get('friendCode') || $cookies.get('influencer') || '';
    $scope.demo.friendBuyReferralCode = $cookies.get('friendCode') || '';
    $scope.demo.influencerReferralCode = $cookies.get('influencer') || '';

    prepTrialUserName();

    // form = 'demoSignUp';
    $scope.step = 'loading';

    scrollToTop();

    if (isKeynote && $scope.demo.emailAddress.match(/keynote\d+@volusion\.net/gi) === null) {
      processError({ message: 'Keynote error on sign up submit', status: 400 });
      return;
    }

    animateProgressBar();

    $scope.demo.attributes[5].value = $scope.demo.country;
    $scope.demo.fullStorySessionUrl = $cookies.get('fs_session_url');

    log.debug({
      category: 'controller',
      message: 'handleSignUpSubmit',
      data: $scope.demo
    });

    if (version === 'v1') {
      createTrial();
      return;
    }

    Trial.hasV2Trial($scope.demo.emailAddress)
      .then((response) => {
        const hasV2Store = response.data || false;

        log.debug({
          category: 'controller',
          message: 'handleSignUpSubmit.hasV2Store',
          data: { hasV2Store }
        });

        if (hasV2Store) {
          processError({ status: 409, trialStatus: 'active', message: 'User has active V2 store' });
          return;
        }

        createTrial();
      })
      // If the V2 store check fails we still continue the process
      .catch(createTrial);
  };

  /**
   * Kick off the trial creation process
   */
  const createTrial = (kind = 'form') => {
    log.debug({
      category: 'controller',
      message: 'createTrial',
      data: { version }
    });

    if (version === 'v2') {
      $scope.demo.salesforce = ThirdParty.buildSalesforceObject($scope.demo);
    }

    Trial.create(version, $scope.demo)
      .then((kind === 'form') ? processCreateResponse : processCreateSocialResponse)
      .catch(processError);
  };

  /**
   * Handle the create process response
   *
   * @param response
   */
  const processCreateResponse = (response) => {
    log.debug({
      category: 'controller',
      message: 'processCreateResponse',
      data: response
    });

    if (response.status !== 201 && response.status !== 202) {
      processError(response);
      return;
    }

    if (version === 'v1') {
      Login.v1($scope.demo)
        .then(processv1LoginResponse)
        .catch(processError);

      return;
    }

    tenantId = response.data.tenantId;
    createdOn = Date.now();

    Trial.storeInfo(tenantId)
      .then((storeInfo) => {
        storeName = storeInfo.data.name;
        storeUrl = storeInfo.data.storeUrl;
        createdOn = storeInfo.data.createdOn;

        Login.v2($scope.demo)
          .then(processv2LoginResponse)
          .catch(processError);
      })
      // We don't really care if there's an error on the storeinfo service.
      // We'll still move forward with the login because we have a tenant ID.
      .catch(() => {
        Login.v2($scope.demo)
          .then(processv2LoginResponse)
          .catch(processError);
      });
  };

  /**
   * Handle the v1 login response
   *
   * @param response
   */
  const processv1LoginResponse = (response) => {
    log.debug({
      category: 'controller',
      message: 'processV1LoginResponse',
      data: response
    });

    if (response.data.d.LoginToken) {
      $cookies.put('DemoLoginToken', response.data.d.LoginToken, cookieDefaults);
    }

    $cookies.put('trialState', 'counter=-1', cookieDefaults);

    const mergedResponse = ThirdParty.mergeResponseObject(response, version, $scope.demo, '', '', '', '', optimizely);

    ThirdParty.loadTrackers();
    ThirdParty.sendAnalytics(mergedResponse);
    BigQuery.sendV1MrktBQTrialCreate(mergedResponse);
    ThirdParty.sendV1Growsumo(mergedResponse);
    pardot.sendV1(mergedResponse);

    let redirect = getFormattedUrl({
      domain: mergedResponse.data.d.DomainName,
      sessionToken: mergedResponse.data.d.SessionToken,
      ip: mergedResponse.data.d.IPAddress
    });

    if ($scope.demo.loginRedirect) {
      redirect = redirect.replace(/\/admin\/|\/+$/, '');
      redirect += $scope.demo.loginRedirect;
    }

    // Need some kind of a fallback in case the redirect URL is borked
    log.debug({
      category: 'controller',
      message: 'processV1LoginResponse.redirect'
    });

    $window.location.href = redirect || '/login';
  };

  /**
   * Handle the v2 login response
   *
   * @param response
   */
  const processv2LoginResponse = (response) => {
    log.debug({
      category: 'controller',
      message: 'processV2LoginResponse',
      data: response
    });

    if (!response.data.id_token || !response.data.access_token) {
      processError({ message: 'No ID/access token from login response', status: 400 });
      return;
    }

    const mergedResponse = ThirdParty.mergeResponseObject(
      response,
      version,
      $scope.demo,
      storeName,
      storeUrl,
      tenantId,
      createdOn,
      optimizely
    );

    ThirdParty.loadTrackers();
    ThirdParty.sendAnalytics(mergedResponse);
    ThirdParty.sendV2Growsumo(mergedResponse);
    Promise.all([
      BigQuery.sendV2MrktBQTrialCreate(mergedResponse),
      ThirdParty.sendV2Warehouse(mergedResponse),
      pardot.sendV2(mergedResponse)
    ])
      .catch((e) => {
        log.exception(e);
      })
      .then(() => {
        $cookies.put('id_token', mergedResponse.data.id_token, cookieDefaults);
        $cookies.put('access_token', mergedResponse.data.id_token, cookieDefaults); // Hack - Seth approved

        log.debug({
          category: 'controller',
          message: 'processV2LoginResponse.redirect'
        });

        $window.location.href = process.env.TRIALS.V2_LOGIN_URL;
      });
  };

  /**
   * Animates the loader progress bar
   */
  const animateProgressBar = () => {
    log.debug({
      category: 'controller',
      message: 'animateProgressBar'
    });

    const maxIncrementVal = (version === 'v1') ? 9 : 15;

    $scope.tick = $interval(() => {
      $scope.progressValue += Math.floor(Math.random() * (maxIncrementVal - 2)) + 2;

      if ($scope.progressValue < 100) {
        $scope.progressStyle = { right: `${100 - $scope.progressValue}%` };
        return;
      }

      $scope.progressValue = 100;
      $scope.progressStyle = { right: '0%' };
      $interval.cancel($scope.tick);
    }, 1000);
  };

  /**
   * Resets the progress bar
   */
  const resetProgressBar = () => {
    log.debug({
      category: 'controller',
      message: 'resetProgressBar'
    });

    $scope.progressStyle = { right: '100%' };
    $scope.progressValue = 0;
    $interval.cancel($scope.tick);
  };

  /**
   * Scroll to the top of the form
   */
  const scrollToTop = () => {
    log.debug({
      category: 'controller',
      message: 'scrollToTop'
    });
    const offset = document.querySelector('.free-trial-form').offsetTop;
    $window.scroll(0, offset);
  };


  /**
   * -----------------------------------------------------------------
   * ERROR HANDLING
   * -----------------------------------------------------------------
   */

  /**
   * Adjusts the error message based on returned status codes,
   * and backs out of the loading screen
   *
   * @param response
   */
  const processError = (response) => {
    scrollToTop();
    try {
      response = normalize(response);
    } catch (e) {
      log.exception(e);
    }

    let message = 'There was an error creating your trial. Please try again.';

    // Handle email validation errors
    if (has(response, 'data')
        && has(response.data, 'emailValidationResult')
        && response.data.emailValidationResult !== null) {
      message = 'The provided email address does not appear to be valid. Please try a different email address.';
      $scope.DemoSignUp.email.$setValidity('pattern', false);
    }

    // v1 -> my.volusion.com
    // v2 -> admin.volusion.com/login
    switch (response.status) {
      case 409: {
        const trialStatus = response.data.trialStatus || 'active';
        log.debug({
          category: 'controller',
          message: 'processError.409',
          data: {
            status: trialStatus
          }
        });

        if (trialStatus !== undefined && trialStatus.includes('expired')) {
          $scope.step = 'expired';
        } else if (trialStatus !== undefined && trialStatus.includes('deleted')) {
          $scope.step = 'deleted';
        } else { // Active is the only one left
          $scope.redirectMsg = 'You have an active trial. Redirecting to login...';
          $scope.step = 'redirect';
          $timeout(() => {
            log.info('redirecting...');
            $window.location.href = `/login/${version}`;
          }, 3000);
        }

        break;
      }
      default:
        log.debug({
          category: 'controller',
          message: 'processError.404',
          data: { message }
        });
        $scope.step = 'form';
        $scope.error = true;
        $scope.errorMessage = message;
    }

    widgetExpire();
    resetProgressBar();
  };

  // Functions
  $scope.checkFormValidity = checkFormValidity;
  $scope.widgetCreate = widgetCreate;
  $scope.facebookLogin = facebookLogin;
  $scope.googleLogin = googleLogin;
  $scope.linkedInLogin = linkedInLogin;
  $scope.widgetExpire = widgetExpire;
  $scope.widgetInvoke = widgetInvoke;
  $scope.widgetSuccess = widgetSuccess;

  /**
   * -----------------------------------------------------------------
   * INITIALIZATION AND STARTUP
   * -----------------------------------------------------------------
   */

  /**
   * Initialize the trial form
   */
  this.$onInit = () => {
    log.debug({
      category: 'controller',
      message: '$onInit'
    });

    setDevice();
    checkVersion();

    // Deep Linking Logic, writes cookie based on query string or javascript var
    if (version === 'v1') {
      // Redirect
      $scope.demo.loginRedirect = $window.loginRedirect || getParamByName('loginRedirect') || '';
    } else if (version === 'v2') {
      if ($window.loginredirect) {
        $cookies.put('loginredirect', $window.loginredirect, cookieDefaults);
      } else if (getParamByName('loginredirect')) {
        $cookies.put('loginredirect', getParamByName('loginredirect'), cookieDefaults);
      }
    }

    // Override recaptcha response if this is a keynote test
    if (isKeynote) $scope.demo.recaptcha = 'afcb3e5a3a944065a487b31ed7f87d3d';

    // If we have a hash, then we're dealing with SSO
    if (isSsoRedirect()) handleSsoRedirect();
  };
}

freeTrialController.$inject = [
  '$scope',
  '$window',
  '$timeout',
  '$interval',
  '$cookies',
  '$location',
  'Login',
  'Trial',
  'ThirdParty',
  'BigQuery',
  'vcRecaptchaService'
];

export default freeTrialController;
